import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu, X } from "lucide-react";
import { NAV_LINKS, SERVICES_MENU, COMPANY } from "../../lib/content";
import useScrolled from "../../hooks/useScrolled";
import usePrefersReducedMotion from "../../hooks/usePrefersReducedMotion";

const slugify = (label) => label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const BrandLockup = ({ compact = false }) => (
  <span className="group inline-flex items-center gap-[10px]">
    <img
      src="/brand/lykit-mark.png"
      alt=""
      aria-hidden="true"
      className="block w-auto transition-opacity duration-300"
      style={{ height: compact ? 17 : 20, opacity: 0.96 }}
    />
    <span className="flex flex-col justify-center">
      <span
        className="font-display font-extrabold leading-none border-b border-transparent group-hover:border-[rgba(216,183,122,0.85)] transition-colors duration-300"
        style={{ fontSize: compact ? 16 : 17.5, letterSpacing: "-0.015em", color: "#F7F6F2" }}
      >
        LYKIT
      </span>
      <span
        className="hidden sm:block leading-none"
        style={{
          fontSize: 7,
          letterSpacing: "0.24em",
          marginTop: 4,
          color: "rgba(247,246,242,0.55)",
          fontWeight: 500,
        }}
      >
        {COMPANY.wordmarkSub}
      </span>
    </span>
  </span>
);

export default function Navbar() {
  const scrolled = useScrolled(18);
  const reduced = usePrefersReducedMotion();
  const { pathname } = useLocation();
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const servicesRef = useRef(null);
  const closeTimer = useRef(null);

  const onServices = pathname.startsWith("/services");

  const isActive = useCallback(
    (to) => (to === "/" ? pathname === "/" : pathname.startsWith(to)),
    [pathname],
  );

  useEffect(() => {
    setServicesOpen(false);
    setMobileOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.classList.toggle("body-lock", mobileOpen);
    return () => document.body.classList.remove("body-lock");
  }, [mobileOpen]);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key !== "Escape") return;
      setServicesOpen(false);
      setMobileOpen(false);
    };
    const onClick = (event) => {
      if (servicesRef.current && !servicesRef.current.contains(event.target)) setServicesOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, []);

  const openServices = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setServicesOpen(true);
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setServicesOpen(false), 140);
  };

  const pillStyle = {
    height: scrolled ? 54 : 64,
    paddingInline: scrolled ? 16 : 22,
    background: scrolled ? "rgba(14,13,20,0.92)" : "rgba(14,13,20,0.78)",
    borderColor: scrolled ? "rgba(216,183,122,0.24)" : "rgba(247,246,242,0.14)",
    boxShadow: scrolled ? "0 18px 48px rgba(8,7,12,0.42)" : "0 14px 38px rgba(8,7,12,0.3)",
    backdropFilter: "blur(16px) saturate(140%)",
    WebkitBackdropFilter: "blur(16px) saturate(140%)",
    transition:
      "height 280ms var(--ease-out), padding 280ms var(--ease-out), background-color 280ms var(--ease-out), box-shadow 280ms var(--ease-out), border-color 280ms var(--ease-out)",
  };

  return (
    <>
      <header
        data-testid="site-navbar"
        className="fixed inset-x-0 top-0 z-50 pointer-events-none"
        style={{ paddingTop: scrolled ? 12 : 18, transition: "padding-top 280ms var(--ease-out)" }}
      >
        <nav
          aria-label="Primary"
          className="pointer-events-auto mx-auto w-[92vw] max-w-[1320px]"
        >
          <div
            data-testid="navbar-pill"
            className="flex items-center justify-between rounded-full border"
            style={pillStyle}
          >
            {/* brand */}
            <Link
              to="/"
              data-testid="navbar-brand"
              aria-label={`${COMPANY.shortName} home`}
              className="shrink-0"
            >
              <BrandLockup compact={scrolled} />
            </Link>

            {/* desktop nav */}
            <ul className="hidden lg:flex items-center" style={{ gap: scrolled ? 20 : 24 }}>
              {NAV_LINKS.map((link) => {
                if (link.children) {
                  return (
                    <li
                      key={link.label}
                      ref={servicesRef}
                      className="relative"
                      onMouseEnter={openServices}
                      onMouseLeave={scheduleClose}
                    >
                      <button
                        type="button"
                        data-testid="navbar-services-trigger"
                        aria-expanded={servicesOpen}
                        aria-haspopup="true"
                        onClick={() => setServicesOpen((v) => !v)}
                        className="nav-link relative flex items-center gap-[6px] py-2 transition-colors duration-200"
                        style={{ color: onServices ? "#F7F6F2" : "rgba(247,246,242,0.82)" }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = "#E7D6B2";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = onServices ? "#F7F6F2" : "rgba(247,246,242,0.82)";
                        }}
                      >
                        {link.label}
                        <ChevronDown
                          size={14}
                          strokeWidth={2}
                          aria-hidden="true"
                          style={{
                            transform: servicesOpen ? "rotate(180deg)" : "none",
                            transition: "transform 240ms var(--ease-out)",
                          }}
                        />
                        {onServices && (
                          <span
                            aria-hidden="true"
                            className="absolute left-1/2 -translate-x-1/2 bottom-[-2px] h-[2px] w-[16px] bg-gold"
                          />
                        )}
                      </button>

                      <AnimatePresence>
                        {servicesOpen && (
                          <motion.div
                            initial={{ opacity: 0, y: reduced ? 0 : 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: reduced ? 0 : 6 }}
                            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
                            className="absolute left-1/2 top-full z-50 -translate-x-1/2"
                            style={{ paddingTop: 16, width: "min(540px, 88vw)" }}
                          >
                            <div
                              data-testid="navbar-services-dropdown"
                              role="menu"
                              aria-label="Services"
                              className="grid gap-1 rounded-[20px] border p-[14px] sm:grid-cols-2"
                              style={{
                                background: "rgba(18,16,26,0.97)",
                                borderColor: "rgba(247,246,242,0.12)",
                                boxShadow: "0 22px 60px rgba(8,7,12,0.45)",
                                backdropFilter: "blur(18px)",
                              }}
                            >
                              {SERVICES_MENU.map((item) => (
                                <Link
                                  key={item.to}
                                  to={item.to}
                                  role="menuitem"
                                  data-testid={`navbar-services-item-${slugify(item.label)}`}
                                  onClick={() => setServicesOpen(false)}
                                  className="group relative block rounded-[14px] px-4 py-3 transition-colors duration-200"
                                  style={{ background: "transparent" }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.background = "rgba(247,246,242,0.08)";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background = "transparent";
                                  }}
                                >
                                  <span
                                    aria-hidden="true"
                                    className="absolute left-0 top-1/2 h-0 w-[2px] -translate-y-1/2 bg-gold transition-all duration-300 group-hover:h-[60%]"
                                  />
                                  <span className="block text-[13.5px] font-medium" style={{ color: "#F7F6F2" }}>
                                    {item.label}
                                  </span>
                                  <span
                                    className="mt-[3px] block text-[11.5px] leading-snug"
                                    style={{ color: "rgba(247,246,242,0.56)" }}
                                  >
                                    {item.desc}
                                  </span>
                                </Link>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </li>
                  );
                }

                const active = isActive(link.to);
                return (
                  <li key={link.label} className="relative">
                    <Link
                      to={link.to}
                      data-testid={`navbar-link-${slugify(link.label)}`}
                      aria-current={active ? "page" : undefined}
                      className="nav-link relative block py-2 transition-colors duration-200"
                      style={{ color: active ? "#F7F6F2" : "rgba(247,246,242,0.82)" }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = "#E7D6B2";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = active ? "#F7F6F2" : "rgba(247,246,242,0.82)";
                      }}
                    >
                      {link.label}
                      {active && (
                        <span
                          aria-hidden="true"
                          className="absolute left-1/2 -translate-x-1/2 bottom-[-2px] h-[2px] w-[16px] bg-gold"
                        />
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>

            {/* right side */}
            <div className="flex items-center gap-2">
              <Link
                to="/contact"
                data-testid="navbar-lets-collaborate-cta"
                className="hidden sm:inline-flex items-center justify-center rounded-full font-bold uppercase transition-all duration-300"
                style={{
                  background: "#F7F6F2",
                  color: "#171522",
                  height: scrolled ? 38 : 42,
                  paddingInline: scrolled ? 18 : 22,
                  fontSize: 11,
                  letterSpacing: "0.12em",
                  boxShadow: "0 10px 24px rgba(8,7,12,0.34)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#E7D6B2";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#F7F6F2";
                }}
              >
                LET'S COLLABORATE
              </Link>

              <button
                type="button"
                data-testid="navbar-mobile-menu-button"
                aria-label="Open menu"
                aria-expanded={mobileOpen}
                onClick={() => setMobileOpen(true)}
                className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-full border transition-colors duration-200"
                style={{ borderColor: "rgba(247,246,242,0.24)" }}
              >
                <Menu size={18} strokeWidth={1.6} style={{ color: "#F7F6F2" }} />
              </button>
            </div>
          </div>
        </nav>
      </header>

      {/* mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            className="fixed inset-0 z-[60] lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.24 }}
          >
            <div
              className="absolute inset-0"
              style={{ background: "rgba(14,13,20,0.5)", backdropFilter: "blur(6px)" }}
              onClick={() => setMobileOpen(false)}
              aria-hidden="true"
            />
            <motion.div
              data-testid="mobile-menu-panel"
              role="dialog"
              aria-modal="true"
              aria-label="Menu"
              initial={{ opacity: 0, y: reduced ? 0 : -10, scale: reduced ? 1 : 0.99 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: reduced ? 0 : -8, scale: reduced ? 1 : 0.995 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="absolute inset-[12px] flex flex-col overflow-y-auto rounded-[26px] border"
              style={{
                background: "#12101A",
                borderColor: "rgba(247,246,242,0.12)",
                boxShadow: "0 22px 60px rgba(8,7,12,0.5)",
              }}
            >
              <div className="flex items-center justify-between px-6 py-5">
                <BrandLockup />
                <button
                  type="button"
                  data-testid="mobile-menu-close"
                  aria-label="Close menu"
                  onClick={() => setMobileOpen(false)}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full border"
                  style={{ borderColor: "rgba(247,246,242,0.22)" }}
                >
                  <X size={18} strokeWidth={1.6} style={{ color: "#F7F6F2" }} />
                </button>
              </div>

              <div className="h-[1px] w-full" style={{ background: "rgba(247,246,242,0.1)" }} />

              <ul className="flex flex-1 flex-col px-6 py-4">
                {NAV_LINKS.map((link, i) => (
                  <motion.li
                    key={link.label}
                    initial={{ opacity: 0, y: reduced ? 0 : 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: reduced ? 0 : 0.06 + i * 0.04, duration: 0.34, ease: [0.16, 1, 0.3, 1] }}
                    className="border-b"
                    style={{ borderColor: "rgba(247,246,242,0.08)" }}
                  >
                    <Link
                      to={link.to}
                      data-testid={`mobile-nav-link-${slugify(link.label)}`}
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center justify-between py-[14px] font-display text-[17px] font-extrabold tracking-[-0.02em]"
                      style={{ color: "#F7F6F2" }}
                    >
                      {link.label}
                      {isActive(link.to) && <span className="h-[6px] w-[6px] rounded-full bg-gold" aria-hidden="true" />}
                    </Link>
                    {link.children && (
                      <ul className="pb-4">
                        {link.children.map((child) => (
                          <li key={child.to}>
                            <Link
                              to={child.to}
                              data-testid={`mobile-services-item-${slugify(child.label)}`}
                              onClick={() => setMobileOpen(false)}
                              className="block py-[7px] text-[13.5px]"
                              style={{ color: "rgba(247,246,242,0.62)" }}
                            >
                              {child.label}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </motion.li>
                ))}
              </ul>

              <div className="px-6 pb-7 pt-2">
                <Link
                  to="/contact"
                  data-testid="mobile-lets-collaborate-cta"
                  onClick={() => setMobileOpen(false)}
                  className="btn btn-ivory w-full"
                >
                  LET'S COLLABORATE
                </Link>
                <a
                  href={`mailto:${COMPANY.email}`}
                  className="mt-4 block text-center text-[12.5px]"
                  style={{ color: "rgba(247,246,242,0.56)" }}
                >
                  {COMPANY.email}
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
